<script>
import { getNumber } from '@src/utils/Format.js'
import { ACTION, TECH_CONTACT_NUMBER } from '@constants/Text'
import { whatsApp } from '@constants/URL'
import { GlobalBus } from '@services/bus'
import {
  MAP_MODULE_BACKGROUND_IMAGE,
  DEFAULT_IMAGE_SRC,
} from '@modules/global/constants/Text.js'

export default {
  name: 'IconNoModuleAccess',
  props: {
    iconSize: { type: [Number, String], default: 16 },
    noBackground: { type: Boolean, default: false },
    disableInteraction: { type: Boolean, default: false },
    module: { type: String, default: null },
    disableModuleImage: { type: Boolean, default: false },
    lockNavBarOnHover: { type: Boolean, default: false },
    // Classe extra pro content do v-menu — permite quem consome o componente
    // sobrescrever z-index/posição sem mexer no CSS global daqui (ex: navbar).
    contentClass: { type: String, default: '' },
    subtitle: {
      type: String,
      default:
        'Este módulo está disponível para upgrade no seu escritório. Clique em Saiba mais para ativar e começar a utilizá-lo.',
    },
  },
  data: () => ({
    MAP_MODULE_BACKGROUND_IMAGE,
    DEFAULT_IMAGE_SRC,
    TIME_TO_CLOSE: 125,
    isHovering: false,
    menuActive: false,
    hoverLockTimeout: null,
    baseWhatsappMessage: 'Olá! Gostaria de saber mais sobre o módulo',
  }),
  computed: {
    containerStyle() {
      if (this.noBackground) return {}

      const size = getNumber(this.iconSize)

      return {
        height: `${size * 1.375}px`,
        width: `${size * 2.0625}px`,
        borderRadius: `${size * 0.75}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#BCBBBB33',
      }
    },
    activatorStyle() {
      const baseStyle = { ...this.containerStyle }

      if (!this.disableInteraction && !this.noBackground && this.isHovering) {
        return { ...baseStyle, backgroundColor: '#BCBBBB66' }
      }

      return baseStyle
    },
    whatsappLink() {
      return whatsApp.link(this.whatsappMessage, TECH_CONTACT_NUMBER)
    },
    title() {
      if (!this.module) return 'Módulo disponível'
      return `Módulo ${this.module}`
    },
    whatsappMessage() {
      if (!this.module) return `${this.baseWhatsappMessage}.`
      return `${this.baseWhatsappMessage} ${this.module}.`
    },
    imageSrc() {
      const map = this.MAP_MODULE_BACKGROUND_IMAGE[this.module] || {}
      return map.src || this.DEFAULT_IMAGE_SRC
    },
    imageStyle() {
      const map = this.MAP_MODULE_BACKGROUND_IMAGE[this.module] || {}

      if (map.offsetY) {
        return { transform: `translateY(${map.offsetY})` }
      }
      return {}
    },
    nudgeRight() {
      const size = getNumber(this.iconSize)
      return 40 * Math.pow(size / 14, 0.9)
    },
    nudgeTop() {
      if (this.disableModuleImage) return 110
      return 175
    },
  },
  watch: {
    menuActive(isActive) {
      this.handleNavHoverLock(isActive)
    },
  },
  beforeDestroy() {
    if (!this.lockNavBarOnHover) return

    this.clearHoverLockTimeout()
    this.toggleNavHoverLock(false)
  },
  methods: {
    mergeMenuListeners(menuOn = {}) {
      return {
        ...menuOn,
        mouseenter: (event) => {
          this.setHoverState(true)
          menuOn.mouseenter && menuOn.mouseenter(event)
        },
        mouseleave: (event) => {
          this.setHoverState(false)
          menuOn.mouseleave && menuOn.mouseleave(event)
        },
      }
    },
    setHoverState(isHovering) {
      if (this.disableInteraction) return

      this.isHovering = isHovering
    },
    openWhatsapp() {
      if (typeof window !== 'undefined' && window.open) {
        window.open(this.whatsappLink, '_blank', 'noopener,noreferrer')
      }
    },
    handleNavHoverLock(shouldLock) {
      if (!this.lockNavBarOnHover) return

      this.clearHoverLockTimeout()

      if (shouldLock) {
        this.toggleNavHoverLock(true)
        return
      }

      this.setDelayToCloseNavBar()
    },
    clearHoverLockTimeout() {
      if (this.hoverLockTimeout) {
        clearTimeout(this.hoverLockTimeout)
        this.hoverLockTimeout = null
      }
    },
    setDelayToCloseNavBar() {
      this.hoverLockTimeout = setTimeout(() => {
        this.hoverLockTimeout = null
        this.toggleNavHoverLock(false)
      }, this.TIME_TO_CLOSE)
    },
    toggleNavHoverLock(isLocked) {
      if (!this.lockNavBarOnHover) return

      GlobalBus.$emit(ACTION.NAVBAR_HOVER_LOCK, isLocked)
    },
  },
}
</script>

<template>
  <div>
    <template v-if="disableInteraction">
      <div :style="activatorStyle">
        <icon-svg name="crown" :size="iconSize" />
      </div>
    </template>

    <template v-else>
      <div class="d-flex align-center">
        <v-menu
          v-model="menuActive"
          max-width="300"
          offset-y
          :nudge-right="nudgeRight"
          :nudge-top="nudgeTop"
          open-on-hover
          :close-delay="TIME_TO_CLOSE"
          :close-on-content-click="false"
          :content-class="contentClass"
        >
          <template v-slot:activator="{ on, attrs }">
            <div
              v-bind="attrs"
              :style="activatorStyle"
              v-on="mergeMenuListeners(on)"
            >
              <icon-svg name="crown" :size="iconSize" />
            </div>
          </template>

          <div class="pa-4 no-module-access-container">
            <p class="ma-0 no-module-access-title">{{ title }}</p>

            <v-divider class="mt-2 mb-4" />

            <div
              v-if="!disableModuleImage"
              class="no-module-access-image-container rounded-sm"
            >
              <v-img width="100%" cover :src="imageSrc" :style="imageStyle">
                <template v-slot:placeholder>
                  <div class="d-flex justify-center">
                    <div class="mt-12">
                      <v-progress-circular indeterminate color="primary" />
                    </div>
                  </div>
                </template>
              </v-img>
            </div>

            <p class="ma-0 my-4 no-module-access-subtitle">
              {{ subtitle }}
            </p>
            <BaseButton minWidth="100%" primary @click="openWhatsapp">
              Saiba mais
            </BaseButton>
          </div>
        </v-menu>
      </div>
    </template>
  </div>
</template>

<style lang="sass">
.no-module-access-container
  background-color: #EEEEEE

  .no-module-access-title
    color: #5B5B5B
    font-size: 14px
    font-weight: 700
    line-height: 20px

  .no-module-access-subtitle
    color: #030309
    font-size: 14px
    font-weight: 300
    line-height: 1.2
    text-align: justify

  .no-module-access-image-container
    height: 140px
    overflow-y: hidden
    position: relative
</style>
